import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
} from '@mui/material';
import { Search as SearchIcon, Print as PrintIcon } from '@mui/icons-material';
import { stockExitsAPI, clientsAPI } from '../services/api';
import { StockExitPaymentSummary } from '../types/stockExitNew';
import { getModuleTableTheme } from '../theme/tableTheme';
import useAlert from '../hooks/useAlert';
import usePdfPreview from '../hooks/usePdfPreview';
import AlertDialog from './AlertDialog';
import { extractErrorMessage } from '../utils/errorHandler';

const STOCK_COLOR = '#2196F3';

const fmt = (v: any): string =>
  `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;

interface ClientOption {
  id: number;
  name: string;
  code?: string;
}

const StockExitPaymentSummaryTab: React.FC = () => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [client, setClient] = useState<ClientOption | null>(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [summary, setSummary] = useState<StockExitPaymentSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { alertState, showError, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const rows = summary?.items ?? [];
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };

  const handlePrint = () => {
    const chosen = rows.filter((r) => selected.has(r.id));
    if (chosen.length === 0) return;
    openPreview({
      getBlob: async () => {
        const { generateStockExitPaymentSummaryPDF } = await import('../utils/pdfGenerator');
        return generateStockExitPaymentSummaryPDF(
          chosen,
          { client_name: client?.name, start_date: startDate || undefined, end_date: endDate || undefined },
          true
        ) as Blob;
      },
      fileName: `Recapitulatif_Paiements_${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  useEffect(() => {
    clientsAPI
      .getAllForDropdown()
      .then((data: any) => setClients(data))
      .catch((e: any) => showError(extractErrorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await stockExitsAPI.getPaymentSummary({
        client_id: client?.id,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setSummary(data);
      setSelected(new Set());
    } catch (e: any) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const totals = summary?.totals;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={clients}
              value={client}
              onChange={(_e, v) => setClient(v)}
              getOptionLabel={(o) => (o.code ? `${o.code} - ${o.name}` : o.name)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Client" variant="filled" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Date début"
              type="date"
              variant="filled"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Date fin"
              type="date"
              variant="filled"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ backgroundColor: STOCK_COLOR, '&:hover': { backgroundColor: '#1976D2' } }}
            >
              Afficher
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {totals && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Montant total', value: totals.total_amount, color: '#0d47a1' },
            { label: 'Total avances/paiements', value: totals.total_advance, color: '#2e7d32' },
            { label: 'Reste à payer', value: totals.total_remaining, color: '#f44336' },
          ].map((c) => (
            <Grid item xs={12} sm={4} key={c.label}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography variant="h6" sx={{ color: c.color, fontWeight: 700 }}>
                  {fmt(c.value)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {rows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={selected.size === 0}
            sx={{ backgroundColor: STOCK_COLOR, '&:hover': { backgroundColor: '#1976D2' } }}
          >
            Imprimer la sélection ({selected.size})
          </Button>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ ...getModuleTableTheme('stock'), width: '100%', maxWidth: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>BON N°</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Client</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avances / Paiements</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Reste</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!summary ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Sélectionnez un client et/ou une période, puis cliquez sur Afficher.
                </TableCell>
              </TableRow>
            ) : summary.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucune sortie trouvée.
                </TableCell>
              </TableRow>
            ) : (
              summary.items.map((row) => (
                <TableRow key={row.id} hover selected={selected.has(row.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(row.id)} onChange={() => toggleRow(row.id)} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.date ? new Date(row.date).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>{row.bon_number}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.client_name || '-'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmt(row.total)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmt(row.total_advance)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: Number(row.remaining) > 0 ? '#f44336' : '#4caf50' }}
                  >
                    {fmt(row.remaining)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
      {pdfPreviewElement}
    </Box>
  );
};

export default StockExitPaymentSummaryTab;
